// SignupsPanel — "New sign-ups", shown at the top of Dream Team for staff: people who requested access
// (TMCChat's /register, or TMCHub's own form) and are waiting for approval. Approve = pick their client,
// tick the products they should see, and they get an email that signs them straight in.
import { useEffect, useMemo, useState } from "react";
import { SignupsApi } from "../hooks/useApi.js";

const ago = (iso) => {
  const t = new Date(iso).getTime();
  if (!t || new Date(iso).getFullYear() < 2000) return ""; // no real date from TMCHub
  const m = Math.max(0, (Date.now() - t) / 60000);
  if (m < 60) return `${Math.round(m)}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function SignupsPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [flash, setFlash] = useState("");
  const load = () => SignupsApi.list().then(setData).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);
  if (!data || !data.items?.length) return flash ? <div className="signups__flash">{flash}</div> : null;

  const done = (msg, userId) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 6000);
    setData((d) => d && { ...d, items: d.items.filter((x) => x.userId !== userId) });
  };

  return (
    <div className="waiting-panel signups">
      <button type="button" className="waiting-panel__head" onClick={() => setOpen((o) => !o)}>
        👋 New sign-ups <b>{data.items.length}</b>
        <span className="waiting-panel__hint">waiting for approval: pick their client + products</span>
        <span className="waiting-panel__chev">{open ? "▾" : "▸"}</span>
      </button>
      {flash && <div className="signups__flash">{flash}</div>}
      {open && data.items.map((s) => (
        <SignupRow key={s.userId} s={s} clients={data.clients} products={data.products} onDone={done} />
      ))}
    </div>
  );
}

function SignupRow({ s, clients, products, onDone }) {
  // Best guess from what they typed: a client whose name matches their company or note.
  const guess = useMemo(() => {
    const hay = [s.company, s.note].map(norm).filter(Boolean);
    return clients.find((c) => hay.some((h) => h && (h.includes(norm(c.name)) || norm(c.name).includes(h))))?.id ?? 0;
  }, [s, clients]);
  const [reviewing, setReviewing] = useState(false);
  // Came through a client's sign-up link → that client + the apps they ticked are pre-filled.
  const [clientId, setClientId] = useState(s.requestedClientId || guess);
  const [picked, setPicked] = useState(s.requestedProductIds || []);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [error, setError] = useState("");

  const list = showAll || !clientId ? products : products.filter((p) => p.clientId === Number(clientId));
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  async function approve() {
    setError("");
    if (!Number(clientId)) { setError("Pick their client."); return; }
    if (!picked.length) { setError("Tick at least one product."); return; }
    setBusy(true);
    try {
      const r = await SignupsApi.approve(s.userId, Number(clientId), picked);
      const warn = [
        r.emailed ? "" : " The email couldn't be sent: ask them to sign in with their password.",
        r.failedProductIds?.length ? ` ${r.failedProductIds.length} product link(s) failed: add them in Channel settings.` : "",
      ].join("");
      onDone(`✓ ${s.name} approved${r.emailed ? ", sign-in email sent" : ""}.${warn}`, s.userId);
    } catch (err) {
      setError(err.body?.error || "Could not approve. Try again.");
      setBusy(false);
    }
  }

  async function decline() {
    if (!confirmDecline) { setConfirmDecline(true); return; }
    setBusy(true);
    try {
      await SignupsApi.reject(s.userId);
      onDone(`${s.name} declined.`, s.userId);
    } catch (err) {
      setError(err.body?.error || "Could not decline. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className="signups__row">
      <div className="signups__who">
        <span className="signups__name"><b>{s.name}</b> · {s.email}</span>
        <span className="signups__meta">
          {s.company ? <>🏢 {s.company}</> : <i>No company given</i>}
          {s.note ? <> · {s.note}</> : null}{ago(s.createdAt) ? ` · ${ago(s.createdAt)}` : ""}
        </span>
        {s.requestedProductIds?.length > 0 && (
          <span className="signups__meta">
            Asked for: {s.requestedProductIds.map((id) => products.find((p) => p.productId === id)?.name || `#${id}`).join(", ")}
          </span>
        )}
      </div>
      {!reviewing ? (
        <div className="signups__actions">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setReviewing(true)}>Review</button>
        </div>
      ) : (
        <div className="signups__form">
          <label className="field__label">Client</label>
          <select className="input select" value={clientId} onChange={(e) => { setClientId(e.target.value); setPicked([]); }}>
            <option value={0}>Choose client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="signups__prodhead">
            <span className="field__label">Products they can see</span>
            <label className="signups__all"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> show all products</label>
          </div>
          <div className="signups__products">
            {list.length === 0 && <span className="signups__empty">No products for this client yet. Tick "show all products".</span>}
            {list.map((p) => (
              <label key={p.productId} className="signups__prod">
                <input type="checkbox" checked={picked.includes(p.productId)} onChange={() => toggle(p.productId)} />
                <span>{p.name}{(showAll || !Number(clientId)) && p.clientName ? <i> · {p.clientName}</i> : null}</span>
              </label>
            ))}
          </div>
          {error && <div className="field__err">⚠ {error}</div>}
          <div className="signups__actions">
            <button type="button" className="btn btn--primary btn--sm" onClick={approve} disabled={busy}>Approve &amp; email sign-in link</button>
            <button type="button" className={`btn btn--sm ${confirmDecline ? "btn--danger" : "btn--soft"}`} onClick={decline} disabled={busy}>
              {confirmDecline ? "Sure? Decline" : "Decline"}
            </button>
            <button type="button" className="btn btn--soft btn--sm" onClick={() => { setReviewing(false); setConfirmDecline(false); setError(""); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
